import { useCallback, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Container } from '@carouselgg/nexus/container'
import { Text } from '@carouselgg/nexus/text'
import { Button } from '@carouselgg/nexus/button'
import { LogoMarkWhite } from '@carouselgg/nexus/brand'
import { ConfirmDialog } from '@carouselgg/nexus/confirm-dialog'
import { Tabs, TabList, Tab } from '@carouselgg/nexus/tabs'
import { toast } from '@carouselgg/nexus/toast'
import { Bell, CheckCheck, Settings, Trash2, MailOpen, X } from '@carouselgg/nexus/icon'
import { buildSections, inTab, notificationAction, notificationTypeIcon, relativeTime } from '@carouselgg/nexus/notification-center'
import type { Notification, NotificationRow, NotificationTab } from '@carouselgg/nexus/notification-center'
import { SwipeableRow } from '@carouselgg/nexus/swipeable-row'
import { useInfiniteScroll, InfiniteScrollFooter } from '@carouselgg/nexus/infinite-scroll'
import { useAppNotifications } from '../context/NotificationContext'
import styles from './NotificationsPage.module.css'

/** Who sent it: the org's logo, its initial, or the Carousel mark for platform notices (no org). */
function SourceTile({ n, children }: { n: Notification; children: ReactNode }) {
  let tile: ReactNode
  if (!n.org.name) {
    tile = <span className={`${styles.tile} ${styles.tileCarousel}`}><img src={LogoMarkWhite} alt="" className={styles.tileMark} /></span>
  } else if (n.org.logoUrl) {
    tile = <img src={n.org.logoUrl} alt="" className={styles.tile} />
  } else {
    tile = <span className={`${styles.tile} ${styles.tileInitial}`}>{n.org.name.charAt(0)}</span>
  }
  return <span className={styles.source}>{tile}<span className={styles.kind}>{children}</span></span>
}

type ConfirmState =
  | { type: 'single'; row: NotificationRow }
  | { type: 'clearRead'; count: number }
  | { type: 'clearAll'; count: number }
  | null

export function NotificationsPage() {
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    dismiss,
    dismissAll,
    clearRead,
    undoDismiss,
    hasMore,
    isLoadingMore,
    loadMore,
  } = useAppNotifications()

  const listRef = useRef<HTMLDivElement>(null)
  const { pullDistance } = useInfiniteScroll({
    scrollRef: listRef,
    onLoadMore: loadMore,
    hasMore,
    isLoading: isLoadingMore,
    threshold: 0.2,
    rubberBand: true,
  })

  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') ?? 'all') as NotificationTab
  const setTab = (v: string) => setParams(v === 'all' ? {} : { tab: v }, { replace: true })

  const readCount = notifications.filter((n) => n.read).length
  const sections = useMemo(() => buildSections(notifications.filter((n) => inTab(n, tab))), [notifications, tab])
  const needsYouCount = useMemo(
    () => buildSections(notifications.filter((n) => !n.read && notificationAction(n))).reduce((sum, s) => sum + s.rows.length, 0),
    [notifications],
  )
  const [confirm, setConfirm] = useState<ConfirmState>(null)

  const markRowRead = useCallback((row: NotificationRow) => {
    row.ids.forEach((id) => markRead(id))
  }, [markRead])

  const handleClick = useCallback((row: NotificationRow) => {
    const n = row.lead
    markRowRead(row)
    // A message opens its conversation; everything else opens its detail.
    const kind: string = n.type
    navigate(kind === 'team_contact_message' && n.href ? n.href : `/notifications/${n.id}`)
  }, [markRowRead, navigate])

  const handleSwipeDismiss = useCallback((row: NotificationRow) => {
    row.ids.forEach((id) => dismiss(id))
    toast(row.count === 1 ? 'Notification deleted' : `${row.count} notifications deleted`, {
      action: { label: 'Undo', onClick: () => row.ids.forEach((id) => undoDismiss(id)) },
      duration: 5000,
    })
  }, [dismiss, undoDismiss])

  const handleConfirm = useCallback(() => {
    if (!confirm) return
    if (confirm.type === 'single') confirm.row.ids.forEach((id) => dismiss(id))
    else if (confirm.type === 'clearRead') clearRead()
    else if (confirm.type === 'clearAll') dismissAll()
    setConfirm(null)
  }, [confirm, dismiss, clearRead, dismissAll])

  const confirmTitle = confirm?.type === 'single'
    ? 'Delete notification?'
    : confirm?.type === 'clearRead'
      ? 'Clear read notifications?'
      : 'Clear all notifications?'

  const confirmDescription = confirm?.type === 'single'
    ? (() => {
        const { lead, count } = confirm.row
        const what = count === 1 ? 'This notification' : `These ${count} notifications`
        return `${what} from ${lead.org.name || 'Carousel'} will be removed.`
      })()
    : confirm?.type === 'clearRead'
      ? `${confirm.count} read notification${confirm.count === 1 ? '' : 's'} will be removed.`
      : confirm
        ? `All ${confirm.count} notification${confirm.count === 1 ? '' : 's'} will be removed.`
        : undefined

  return (
    <Container size="md">
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Text as="h1" size="xl" weight="bold">Notifications</Text>
            {unreadCount > 0 && (
              <span className={styles.unreadCount}>{unreadCount} unread</span>
            )}
          </div>
          <div className={styles.headerRight}>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllRead}>
                <CheckCheck size={14} />
                Mark all read
              </Button>
            )}
            {readCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setConfirm({ type: 'clearRead', count: readCount })}>
                <Trash2 size={14} />
                Clear read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setConfirm({ type: 'clearAll', count: notifications.length })}>
                <Trash2 size={14} />
                Clear all
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/settings/notifications')} aria-label="Notification settings">
              <Settings size={14} />
            </Button>
          </div>
        </div>

        {notifications.length > 0 && (
          <Tabs value={tab} onChange={setTab} className={styles.tabs}>
            <TabList>
              <Tab value="all">All</Tab>
              <Tab value="needs-you" badge={needsYouCount || undefined}>Needs you</Tab>
              <Tab value="competitions">Competitions</Tab>
              <Tab value="teams">Teams &amp; clubs</Tab>
              <Tab value="carousel">Carousel</Tab>
            </TabList>
          </Tabs>
        )}

        {/* List */}
        {notifications.length === 0 ? (
          <div className={styles.empty}>
            <Bell size={40} />
            <Text as="p" size="lg" muted>You're all caught up</Text>
            <Text as="p" size="sm" muted>No notifications to show.</Text>
          </div>
        ) : sections.length === 0 ? (
          <div className={styles.empty}>
            <Text as="p" size="sm" muted>
              {tab === 'needs-you' ? 'Nothing is waiting on you.' : 'No notifications here.'}
            </Text>
          </div>
        ) : (
          <div className={styles.list} ref={listRef}>
            {sections.map((section) => (
              <section key={section.label} className={styles.section} aria-label={section.label}>
                <h2 className={styles.sectionLabel}>{section.label}</h2>
                {section.rows.map((row) => {
                  const n = row.lead
                  const Icon = notificationTypeIcon(n.type)
                  const action = notificationAction(n)
                  return (
                    <SwipeableRow
                      key={n.id}
                      onDelete={() => handleSwipeDismiss(row)}
                      onAction={() => markRowRead(row)}
                      showAction={!row.read}
                      actionLabel="Read"
                      deleteLabel="Delete"
                    >
                      <div className={`${styles.item} ${!row.read ? styles.itemUnread : ''}`} role="group" aria-label={n.title}>
                        <button className={styles.itemMain} onClick={() => handleClick(row)}>
                          {!row.read && <span className={styles.unreadDot} aria-label="Unread" />}
                          <SourceTile n={n}><Icon size={11} strokeWidth={2.4} /></SourceTile>
                          <span className={styles.itemContent}>
                            <span className={styles.itemTitle}>
                              {n.title}
                              {row.count > 1 && <span className={styles.repeat}>×{row.count}</span>}
                            </span>
                            <span className={styles.itemMeta}>
                              <span className={styles.metaTime}>{relativeTime(n.createdAt)} · </span>
                              <span className={styles.orgName}>{n.org.name || 'Carousel'}</span>
                              {n.body && <> · {n.body}</>}
                            </span>
                          </span>
                        </button>
                        {action && (
                          <Button
                            variant="secondary"
                            size="sm"
                            className={styles.rowAction}
                            onClick={() => { markRowRead(row); navigate(action.to) }}
                          >
                            <Icon size={14} aria-hidden="true" />
                            {action.label}
                          </Button>
                        )}
                        <span className={styles.time}>{relativeTime(n.createdAt)}</span>
                        <div className={styles.itemActions}>
                          {!row.read && (
                            <button
                              className={styles.actionBtn}
                              onClick={() => markRowRead(row)}
                              aria-label="Mark as read"
                              title="Mark as read"
                            >
                              <MailOpen size={14} />
                            </button>
                          )}
                          <button
                            className={styles.actionBtn}
                            onClick={() => setConfirm({ type: 'single', row })}
                            aria-label="Dismiss notification"
                            title="Dismiss"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    </SwipeableRow>
                  )
                })}
              </section>
            ))}
            <InfiniteScrollFooter
              isLoading={isLoadingMore}
              hasMore={hasMore}
              pullDistance={pullDistance}
            />
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirm?.type === 'single' ? 'Delete' : 'Clear'}
        variant="danger"
      />
    </Container>
  )
}
